import { motion } from 'framer-motion';
import { Palette, Smartphone, Search, MessageSquare } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const content = {
  nl: {
    eyebrow: 'Waarom Netblitz',
    title: 'Een website die niet alleen mooi is, maar ook werkt',
    subtitle: 'Veel ondernemers hebben een website nodig die vertrouwen uitstraalt, goed werkt op mobiel, duidelijk uitlegt wat ze doen en bezoekers omzet in aanvragen. Netblitz combineert design, structuur en conversie in één duidelijke oplossing.',
    services: [
      {
        icon: Palette,
        title: 'Professioneel design',
        description: 'Een moderne website die past bij jouw bedrijf, sector en doelgroep.'
      },
      {
        icon: Smartphone,
        title: 'Mobielvriendelijk',
        description: 'Jouw website ziet er goed uit op smartphone, tablet en desktop.'
      },
      {
        icon: Search,
        title: 'SEO-basis inbegrepen',
        description: 'We zorgen voor een logische structuur, snelle laadtijd en een goede basis om gevonden te worden in Google.'
      },
      {
        icon: MessageSquare,
        title: 'Duidelijke offerteflow',
        description: 'Via contactformulier en chatbot kunnen bezoekers snel informatie opvragen of een offerte aanvragen.'
      }
    ]
  },
  en: {
    eyebrow: 'Why Netblitz',
    title: 'A website that\'s not just beautiful, but also works',
    subtitle: 'Many entrepreneurs need a website that exudes trust, works well on mobile, clearly explains what they do and converts visitors into inquiries. Netblitz combines design, structure and conversion in one clear solution.',
    services: [
      {
        icon: Palette,
        title: 'Professional design',
        description: 'A modern website that fits your company, sector and target audience.'
      },
      {
        icon: Smartphone,
        title: 'Mobile-friendly',
        description: 'Your website looks great on smartphone, tablet and desktop.'
      },
      {
        icon: Search,
        title: 'SEO basics included',
        description: 'We ensure a logical structure, fast loading time and a good foundation for being found in Google.'
      },
      {
        icon: MessageSquare,
        title: 'Clear quote flow',
        description: 'Via contact form and chatbot, visitors can quickly request information or a quote.'
      }
    ]
  },
  fr: {
    eyebrow: 'Pourquoi Netblitz',
    title: 'Un site web qui n\'est pas seulement beau, mais qui fonctionne',
    subtitle: 'De nombreux entrepreneurs ont besoin d\'un site web qui inspire confiance, fonctionne bien sur mobile et convertit les visiteurs en demandes.',
    services: [
      {
        icon: Palette,
        title: 'Design professionnel',
        description: 'Un site web moderne adapté à votre entreprise et à votre secteur.'
      },
      {
        icon: Smartphone,
        title: 'Mobile-friendly',
        description: 'Votre site s\'affiche parfaitement sur tous les appareils.'
      },
      {
        icon: Search,
        title: 'Base SEO incluse',
        description: 'Structure logique et temps de chargement rapide pour être trouvé sur Google.'
      },
      {
        icon: MessageSquare,
        title: 'Flux de devis clair',
        description: 'Les visiteurs peuvent facilement demander des informations ou un devis.'
      }
    ]
  },
  de: {
    eyebrow: 'Warum Netblitz',
    title: 'Eine Website, die nicht nur schön ist, sondern auch funktioniert',
    subtitle: 'Viele Unternehmer brauchen eine Website, die Vertrauen ausstrahlt, gut auf Mobilgeräten funktioniert und Besucher in Anfragen umwandelt.',
    services: [
      {
        icon: Palette,
        title: 'Professionelles Design',
        description: 'Eine moderne Website, die zu Ihrem Unternehmen und Ihrer Branche passt.'
      },
      {
        icon: Smartphone,
        title: 'Mobilfreundlich',
        description: 'Ihre Website sieht auf allen Geräten großartig aus.'
      },
      {
        icon: Search,
        title: 'SEO-Basis inklusive',
        description: 'Logische Struktur und schnelle Ladezeit für bessere Google-Rankings.'
      },
      {
        icon: MessageSquare,
        title: 'Klarer Angebotsablauf',
        description: 'Besucher können einfach Informationen oder ein Angebot anfordern.'
      }
    ]
  }
};

export const ServicesPreview = () => {
  const { language } = useLanguage();
  const t = content[language] || content.nl;

  return (
    <section 
      className="py-20 sm:py-28 bg-[#F3F4F6]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <span 
            className="inline-block px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4 bg-blue-100 text-[#2563EB]"
          >
            {t.eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-[#111827] mb-6 leading-tight">
            {t.title}
          </h2>
          <p className="text-lg text-[#4B5563] leading-relaxed">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors bg-blue-100">
                  <Icon size={24} className="text-[#2563EB]" />
                </div>
                <h3 className="text-lg font-bold text-[#111827] mb-2">{service.title}</h3>
                <p className="text-[#4B5563] text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
